import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer,
} from 'recharts';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';
import { SKI_PLACEHOLDER_IMAGE } from '../lib/placeholder';

// The 6 rating attributes — change in ONE place if you ever add another axis.
const ATTRIBUTES = [
  { key: 'rating_on_piste',     label: 'On Piste' },
  { key: 'rating_off_piste',    label: 'Off Piste' },
  { key: 'rating_trees_bumps',  label: 'Trees & Bumps' },
  { key: 'rating_crud',         label: 'Crud' },
  { key: 'rating_powder',       label: 'Powder' },
  { key: 'rating_stability',    label: 'Stability at Speed' },
];

const NOTE_SELECT = `id, user_name, body, created_at, ${ATTRIBUTES.map(a => a.key).join(', ')}`;

const emptyRatings = () => ATTRIBUTES.reduce((acc, a) => ({ ...acc, [a.key]: 0 }), {});

export default function QuiverDetail() {
  const { id } = useParams();
  const { user } = useAuth();

  const [ski, setSki] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [body, setBody] = useState('');
  const [ratings, setRatings] = useState(emptyRatings);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const loadSki = useCallback(async () => {
    const { data, error } = await supabase
      .from('skis')
      .select('*')
      .eq('id', id)
      .single();
    if (error) { setError(error.message); setSki(null); }
    else setSki(data);
  }, [id]);

  const loadNotes = useCallback(async () => {
    const { data, error } = await supabase
      .from('ski_notes')
      .select(NOTE_SELECT)
      .eq('ski_id', id)
      .order('created_at', { ascending: false });
    if (error) setError(error.message);
    else setNotes(data || []);
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([loadSki(), loadNotes()]).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [loadSki, loadNotes]);

  // Average each attribute across submitted reviews. Returns null if no reviews exist.
  const averages = useMemo(() => {
    if (notes.length === 0) return null;
    const out = {};
    for (const { key } of ATTRIBUTES) {
      const values = notes.map((n) => n[key]).filter((v) => v != null);
      out[key] = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
    }
    return out;
  }, [notes]);

  const radarData = useMemo(() => {
    if (!averages) return ATTRIBUTES.map((a) => ({ subject: a.label, value: 0 }));
    return ATTRIBUTES.map((a) => ({ subject: a.label, value: Number(averages[a.key].toFixed(2)) }));
  }, [averages]);

  // Playful ←→ Carve position lives on the ski itself (skis.playful_to_carve).
  // 0 = ultra playful (wood, surfy, e.g. Bent 100), 1 = ultra carver (titanal, e.g. Mantra).
  // It's a property of construction, not derivable from review averages.
  const playfulCarvePosition = useMemo(() => {
    if (ski?.playful_to_carve != null) return Number(ski.playful_to_carve);
    return 0.5;
  }, [ski]);
  const playfulCarveHasData = ski?.playful_to_carve != null;

  const allRatingsSet = ATTRIBUTES.every((a) => ratings[a.key] >= 1 && ratings[a.key] <= 5);
  const canSubmit = !!user && body.trim().length > 0 && allRatingsSet && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    const payload = {
      ski_id: Number(id),
      user_id: user.id,
      user_name: user.name,
      body: body.trim(),
      ...ratings,
    };
    const { error } = await supabase.from('ski_notes').insert(payload);
    setSubmitting(false);
    if (error) {
      setSubmitError(error.message);
      return;
    }
    setBody('');
    setRatings(emptyRatings());
    setModalOpen(false);
    loadNotes();
  };

  // Close on Escape key when modal is open. Doesn't reset form state — leave
  // body/ratings intact so accidental close doesn't trash a long review.
  useEffect(() => {
    if (!modalOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setModalOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalOpen]);

  if (loading) return <div style={styles.container}><div style={styles.message}>Loading…</div></div>;
  if (error) return <div style={styles.container}><div style={styles.error}>Failed to load: {error}</div></div>;
  if (!ski) return <div style={styles.container}><div style={styles.message}>Ski not found.</div></div>;

  return (
    <div style={styles.container}>
      <Link to="/quiver" style={styles.backLink}>← Back to the quiver</Link>

      {/* Header card: banner image on top, then specs + description */}
      <div style={styles.header}>
        <div style={styles.imageBanner}>
          <img
            src={ski.image_url || SKI_PLACEHOLDER_IMAGE}
            alt={`${ski.brand} ${ski.model}`}
            style={styles.bannerImage}
          />
        </div>
        <div style={styles.headerInfo}>
          <div style={styles.brand}>{ski.brand}</div>
          <h1 style={styles.model}>{ski.model}</h1>
          <div style={styles.specGrid}>
            {ski.waist_width_mm != null && <SpecChip label="Waist" value={`${ski.waist_width_mm}mm`} />}
            {ski.category && <SpecChip label="Category" value={ski.category} />}
            {ski.turn_radius_m != null && <SpecChip label="Radius" value={`${ski.turn_radius_m}m`} />}
            {ski.lengths_cm && ski.lengths_cm.length > 0 && (
              <SpecChip label="Lengths" value={`${ski.lengths_cm.join(', ')}cm`} />
            )}
            {ski.year && <SpecChip label="Season" value={`${ski.year - 1}/${String(ski.year).slice(2)}`} />}
          </div>
          {ski.description && <p style={styles.description}>{ski.description}</p>}
        </div>
      </div>

      {/* Visualization card: Playful↔Carve slider + radar */}
      <div style={styles.vizCard}>
        <h2 style={styles.sectionTitle}>The data</h2>
        {averages ? (
          <div style={styles.vizMeta}>Radar averaged across {notes.length} review{notes.length === 1 ? '' : 's'}</div>
        ) : (
          <div style={styles.vizMeta}>No reviews yet — radar will fill in as people post.</div>
        )}

        <div style={styles.vizGrid}>
          <div style={styles.fbWrap}>
            <div style={styles.fbLabels}>
              <span>Playful</span>
              <span>Carve</span>
            </div>
            <div style={styles.fbTrack}>
              <div style={{ ...styles.fbDot, left: `${playfulCarvePosition * 100}%` }} />
            </div>
            <div style={styles.fbHint}>
              {playfulCarveHasData
                ? 'Wood / surfy on the left, titanal / charging on the right'
                : 'Not yet rated for this ski'}
            </div>
          </div>

          <div style={styles.radarWrap}>
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={radarData} outerRadius="75%">
                <PolarGrid stroke="#E2E8F0" />
                <PolarAngleAxis
                  dataKey="subject"
                  tick={{ fill: '#475569', fontSize: 12, fontWeight: 600 }}
                />
                <PolarRadiusAxis angle={90} domain={[0, 5]} tick={{ fill: '#94A3B8', fontSize: 10 }} />
                <Radar
                  name="Avg"
                  dataKey="value"
                  stroke="#7C3AED"
                  fill="#7C3AED"
                  fillOpacity={0.45}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Reviews / notes */}
      <div style={styles.notesSection}>
        <div style={styles.notesHeader}>
          <h2 style={styles.sectionTitle}>Reviews</h2>
          {user && (
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              style={styles.addReviewButton}
            >
              + Add review
            </button>
          )}
        </div>

        {!user && (
          <div style={styles.signInPrompt}>
            Sign in above to leave a review.
          </div>
        )}

        {notes.length === 0 ? (
          <div style={styles.emptyNotes}>No reviews yet. Be the first.</div>
        ) : (
          <div style={styles.notesList}>
            {notes.map((n) => (
              <div key={n.id} style={styles.note}>
                <div style={styles.noteHeader}>
                  <span style={styles.noteAuthor}>{n.user_name}</span>
                  <span style={styles.noteDate}>{formatDate(n.created_at)}</span>
                </div>
                <div style={styles.noteBody}>{n.body}</div>
                <div style={styles.noteRatings}>
                  {ATTRIBUTES.map((a) => (
                    n[a.key] != null && (
                      <span key={a.key} style={styles.noteRatingChip}>
                        {a.label} <strong>{n[a.key]}</strong>
                      </span>
                    )
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {modalOpen && user && (
        <div
          style={styles.modalBackdrop}
          onClick={(e) => { if (e.target === e.currentTarget) setModalOpen(false); }}
        >
          <div style={styles.modalCard} role="dialog" aria-modal="true">
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Review the {ski.model}</h2>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                style={styles.modalClose}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} style={styles.form}>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder={`What did you think of the ${ski.model}?`}
                rows={4}
                maxLength={2000}
                style={styles.textarea}
                autoFocus
              />

              <div style={styles.ratingsGroup}>
                <div style={styles.ratingsTitle}>Rate it (1 = bad, 5 = great)</div>
                {ATTRIBUTES.map((a) => (
                  <RatingRow
                    key={a.key}
                    label={a.label}
                    value={ratings[a.key]}
                    onChange={(v) => setRatings((r) => ({ ...r, [a.key]: v }))}
                  />
                ))}
              </div>

              <div style={styles.formFooter}>
                <span style={styles.formMeta}>Posting as <strong>{user.name}</strong></span>
                <div style={styles.formActions}>
                  <button
                    type="button"
                    onClick={() => setModalOpen(false)}
                    style={styles.cancelButton}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!canSubmit}
                    style={{
                      ...styles.submitButton,
                      ...(canSubmit ? null : styles.submitButtonDisabled),
                    }}
                  >
                    {submitting ? 'Posting…' : 'Post review'}
                  </button>
                </div>
              </div>
              {submitError && <div style={styles.submitError}>{submitError}</div>}
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function RatingRow({ label, value, onChange }) {
  return (
    <div style={styles.ratingRow}>
      <div style={styles.ratingLabel}>{label}</div>
      <div style={styles.ratingButtons}>
        {[1, 2, 3, 4, 5].map((n) => {
          const selected = value === n;
          return (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              style={{
                ...styles.ratingButton,
                ...(selected ? styles.ratingButtonSelected : null),
              }}
            >
              {n}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const SpecChip = ({ label, value }) => (
  <div style={styles.specChip}>
    <div style={styles.specLabel}>{label}</div>
    <div style={styles.specValue}>{value}</div>
  </div>
);

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
};

const styles = {
  container: { maxWidth: 960, margin: '0 auto', padding: '20px 20px 60px', fontFamily: 'Inter, system-ui, sans-serif' },
  backLink: { display: 'inline-block', color: '#7C3AED', textDecoration: 'none', fontSize: '0.9rem', fontWeight: 600, marginBottom: 20 },

  header: {
    background: 'white',
    borderRadius: 20,
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    overflow: 'hidden',
    marginBottom: 24,
  },
  imageBanner: {
    width: '100%',
    height: 200,
    background: 'linear-gradient(135deg, #F8FAFC, #EEF2FF)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  bannerImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center',
    display: 'block',
  },
  headerInfo: { display: 'flex', flexDirection: 'column', gap: 12, padding: 32 },
  brand: { fontSize: '0.85rem', fontWeight: 700, color: '#7C3AED', textTransform: 'uppercase', letterSpacing: '0.05em' },
  model: { fontSize: '2rem', fontWeight: 800, color: '#1E293B', margin: 0, letterSpacing: '-0.025em' },
  specGrid: { display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 8 },
  specChip: { background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 10, padding: '8px 12px', minWidth: 70 },
  specLabel: { fontSize: '0.7rem', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em' },
  specValue: { fontSize: '0.9rem', fontWeight: 600, color: '#1E293B', marginTop: 2 },
  description: { fontSize: '0.95rem', color: '#475569', lineHeight: 1.55, margin: '12px 0 0' },

  vizCard: {
    background: 'white',
    borderRadius: 20,
    padding: 32,
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    marginBottom: 24,
  },
  vizMeta: { fontSize: '0.85rem', color: '#94A3B8', marginBottom: 24 },
  vizGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32, alignItems: 'center' },

  fbWrap: { padding: '20px 0' },
  fbLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.85rem',
    fontWeight: 700,
    color: '#475569',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 12,
  },
  fbTrack: {
    position: 'relative',
    height: 12,
    background: 'linear-gradient(to right, #DBEAFE, #EDE9FE)',
    borderRadius: 999,
  },
  fbDot: {
    position: 'absolute',
    top: '50%',
    width: 22,
    height: 22,
    borderRadius: '50%',
    background: '#7C3AED',
    border: '3px solid white',
    boxShadow: '0 2px 8px rgba(124,58,237,0.4)',
    transform: 'translate(-50%, -50%)',
    transition: 'left 0.3s ease',
  },
  fbHint: { fontSize: '0.75rem', color: '#94A3B8', marginTop: 16, textAlign: 'center' },

  radarWrap: { width: '100%' },

  notesSection: {
    background: 'white',
    borderRadius: 20,
    padding: 32,
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  },
  notesHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    gap: 12,
  },
  sectionTitle: { fontSize: '1.3rem', fontWeight: 700, color: '#1E293B', margin: '0 0 8px' },
  addReviewButton: {
    background: '#7C3AED',
    color: 'white',
    border: 'none',
    borderRadius: 12,
    padding: '10px 18px',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    flexShrink: 0,
  },

  modalBackdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.55)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '60px 20px 20px',
    zIndex: 1000,
    overflowY: 'auto',
  },
  modalCard: {
    background: 'white',
    borderRadius: 20,
    boxShadow: '0 20px 50px -10px rgba(0, 0, 0, 0.3)',
    width: '100%',
    maxWidth: 560,
    padding: 28,
    fontFamily: 'Inter, system-ui, sans-serif',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  modalTitle: { fontSize: '1.2rem', fontWeight: 700, color: '#1E293B', margin: 0 },
  modalClose: {
    background: 'transparent',
    border: 'none',
    fontSize: '1.8rem',
    lineHeight: 1,
    color: '#94A3B8',
    cursor: 'pointer',
    padding: 4,
    fontFamily: 'inherit',
  },
  formActions: { display: 'flex', gap: 8 },
  cancelButton: {
    background: 'white',
    color: '#64748B',
    border: '1.5px solid #E2E8F0',
    borderRadius: 12,
    padding: '10px 18px',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },

  form: { marginBottom: 28 },
  textarea: {
    width: '100%',
    padding: 14,
    borderRadius: 12,
    border: '2px solid #E2E8F0',
    fontSize: '0.95rem',
    color: '#1E293B',
    fontFamily: 'inherit',
    resize: 'vertical',
    outline: 'none',
    boxSizing: 'border-box',
  },
  ratingsGroup: {
    background: '#F8FAFC',
    borderRadius: 14,
    padding: 18,
    marginTop: 16,
  },
  ratingsTitle: {
    fontSize: '0.78rem',
    fontWeight: 700,
    color: '#64748B',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 12,
  },
  ratingRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '8px 0',
  },
  ratingLabel: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#1E293B',
  },
  ratingButtons: { display: 'flex', gap: 6 },
  ratingButton: {
    width: 34,
    height: 34,
    borderRadius: 10,
    border: '1.5px solid #E2E8F0',
    background: 'white',
    color: '#64748B',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '0.9rem',
  },
  ratingButtonSelected: {
    background: '#7C3AED',
    borderColor: '#7C3AED',
    color: 'white',
  },

  formFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 },
  formMeta: { fontSize: '0.85rem', color: '#64748B' },
  submitButton: {
    background: '#7C3AED',
    color: 'white',
    border: 'none',
    borderRadius: 12,
    padding: '10px 20px',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  submitButtonDisabled: { background: '#CBD5E1', cursor: 'not-allowed' },
  submitError: { color: '#DC2626', fontSize: '0.85rem', marginTop: 8 },

  signInPrompt: {
    background: '#F8FAFC',
    border: '1px dashed #CBD5E1',
    borderRadius: 12,
    padding: 20,
    textAlign: 'center',
    color: '#64748B',
    fontSize: '0.95rem',
    marginBottom: 28,
  },

  emptyNotes: { color: '#94A3B8', fontSize: '0.95rem', padding: '20px 0' },
  notesList: { display: 'flex', flexDirection: 'column', gap: 16 },
  note: {
    border: '1px solid #F1F5F9',
    borderRadius: 14,
    padding: 18,
  },
  noteHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  noteAuthor: { fontWeight: 700, color: '#1E293B', fontSize: '0.95rem' },
  noteDate: { fontSize: '0.8rem', color: '#94A3B8' },
  noteBody: { color: '#475569', fontSize: '0.95rem', lineHeight: 1.55, whiteSpace: 'pre-wrap', marginBottom: 12 },
  noteRatings: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  noteRatingChip: {
    fontSize: '0.75rem',
    color: '#475569',
    background: '#F8FAFC',
    border: '1px solid #E2E8F0',
    borderRadius: 8,
    padding: '4px 8px',
  },

  message: { textAlign: 'center', padding: 60, color: '#64748B' },
  error: { textAlign: 'center', padding: 60, color: '#DC2626' },
};
